import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { vehicleService } from '../../application/VehicleService';
import { SelectionActionType } from '../../context/selection/actions';
import { useSelectionContext } from '../../context/selection/context';
import { Vehicle } from '../../domain/vehicle/Vehicle';

import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { getOrganizationResourcesMutationKey } from '../organization';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveFlatVehicles } from './useRetrieveFlatVehicles';

type DeleteVehiclesParams = { vehicleIds: string[] };

type UseDeleteVehiclesProps = ServiceResponse<({ vehicleIds }: DeleteVehiclesParams) => void, void>;

// Hooks act as 'Adapter' layer.
export const useDeleteVehicles = (): UseDeleteVehiclesProps => {
  const queryClient = useQueryClient();
  const { data: activeProject } = useRetrieveActiveProject();
  const { run: refetchFlatVehicles } = useRetrieveFlatVehicles({ project: activeProject });
  const { selectionState, selectionDispatch } = useSelectionContext();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation<Vehicle[], unknown, DeleteVehiclesParams>(
    ({ vehicleIds }: DeleteVehiclesParams): Promise<Vehicle[]> =>
      vehicleService.deleteVehicles({ vehicleIds, project: activeProject! }),
    {
      onSuccess: async (deletedVehicles: Vehicle[], { vehicleIds }) => {
        const deletedVehiclesIds = deletedVehicles.map((deletedVehicle) => deletedVehicle.id);

        deletedVehiclesIds.map(async (deletedVehiclesId) => {
          await queryClient.invalidateQueries([`planner_vehicle`, deletedVehiclesId]);
        });

        await queryClient.invalidateQueries([`planner_vehicles`]);
        await queryClient.invalidateQueries([`planner_vehicles_enabled`]);
        await queryClient.invalidateQueries([`planner_flat_vehicles`]);
        queryClient.invalidateQueries(getOrganizationResourcesMutationKey());

        if (
          deletedVehiclesIds.some((deletedVehiclesId) => selectionState.selectedVehicles.includes(deletedVehiclesId))
        ) {
          selectionDispatch({
            type: SelectionActionType.SET_SELECTED_VEHICLES,
            payload: {
              selectedVehicles: selectionState.selectedVehicles.filter(
                (selectedVehicle) => !deletedVehiclesIds.includes(selectedVehicle)
              ),
            },
          });
        }

        refetchFlatVehicles();

        if (vehicleIds.length > 0 && deletedVehicles.length === 0) {
          showSnackbarMessage({
            type: `error`,
            messageIntlId: `infoerror.delete_vehicles_error`,
          });
        } else if (deletedVehicles.length !== vehicleIds.length) {
          showSnackbarMessage({
            type: `warning`,
            messageIntlId: `infoerror.delete_vehicles_warning`,
          });
        } else {
          showSnackbarMessage({
            type: `success`,
            messageIntlId: `infoerror.delete_vehicles_success`,
          });
        }
      },
      // If the mutation fails, use the context we returned above
      onError: (err: any, variables: DeleteVehiclesParams, context: any) => {
        console.info(`Delete Vehicles Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ vehicleIds }: DeleteVehiclesParams) => mutate({ vehicleIds }),
  };
};
